<template>
  <div
    v-if="isBusinessAdmin && billingSettings"
    data-component-group="user-settings-panel"
    data-component="billing-accounts"
  >
    <div data-component-group="billing-account-dialog" data-component="add-new">
      <dialog-add
        icon="credit_card"
        :title="$t('billing_account')"
        :is-on="isDialogOpen"
        :on-close="toggleEditDialog"
        :is-close-on-outside-click="false"
      >
        <template v-slot:content>
          <forms-billing-account-billing-methods
            :item="activeItem"
            :is-on="isDialogOpen"
            :is-loading="isLoading"
            :on-cancel="toggleEditDialog"
            :on-confirm="onSubmitBillingAccount"
          />
        </template>
      </dialog-add>
    </div>

    <div class="billing-accounts-wrapper">
      <alerts-base
        v-if="countriesMissingBilling.length"
        type="info"
      >
        <template v-slot:text>{{ $t('you_are_missing_or_need_billing') }} <b>{{ missingItemsNames }}</b>.</template>
      </alerts-base>

      <header>
        {{ $t('billing_accounts') }}
        <v-btn
          color="primary"
          :elevation="0"
          @click="toggleEditDialog"
        >
          <v-icon>add</v-icon>
          {{ $t('add_billing_account') }}
        </v-btn>
      </header>

      <div
        v-if="!billingSettings.length"
        class="empty-billing-accounts"
        @click="toggleEditDialog"
      >
        <v-icon>add</v-icon>
        <div>{{ $t('add_new_billing_account') }}</div>
      </div>

      <div class="account-cards">
        <!--TODO replace index with something more meaningful-->
        <div
          v-for="(item, index) in billingSettings"
          :key="index"
          class="account-card-wrapper"
        >
          <div
            v-if="!item.is_verified"
            class="warning-panel"
          >
            <v-icon
              size="14px"
              color="rgb(255, 182, 18)"
            >
              warning
            </v-icon>
            <span>
              {{ $t('please_update_your_billing_details') }}
            </span>
          </div>

          <div class="advertiser-box-details">
            <div v-if="item.payment_method === 'invoice'">
              <section class="card-name">
                <label>
                  {{ $t('company_name') }}
                </label>
                <div class="card-name-item">
                  {{ item.legal_business_name }}
                </div>
              </section>

              <section class="second-field">
                <label>
                  {{ $t('vat_id') }}
                </label>
                <span class="second-field-item">
                  {{ item.vat_id }}
                </span>
              </section>

              <section class="linked-to">
                <label>
                  {{ $t('linked_to') }}
                </label>
                <div class="linked-to-items">
                    <span v-if="item?.linked_countries && !item.linked_countries.length">
                      -
                    </span>
                    <span
                      v-for="country in item?.linked_countries"
                      :key="country.code"
                      class="linked-item"
                    >
                      <span class="green-dot" />
                      {{ country.name }}
                    </span>
                  </div>
              </section>
            </div>

            <div v-else>
              <section class="card-name">
                <label>
                  {{ $t('network_name') }}
                </label>
                <div class="card-name-item">
                  {{ item.name }}
                </div>
              </section>

              <section class="second-field">
                <label>
                  {{ $t('contact') }}
                </label>
                <span class="second-field-item">
                  {{ item.finance_contact }}
                </span>
              </section>

              <section
                v-if="item.in_review_countries && item.in_review_countries.length"
                class="linked-to"
              >
                <label>
                  {{ $t('in_review_for') }}
                </label>
                <div class="linked-to-items">
                    <span
                      v-for="country in item.in_review_countries"
                      :key="country.code"
                      class="linked-item"
                    >
                      <span class="orange-dot" />
                      {{ country.name }}
                    </span>
                  </div>
              </section>

              <section
                v-if="item?.linked_countries && item?.linked_countries.length"
                class="linked-to"
              >

                <label>
                  {{ $t('linked_to') }}
                </label>
                <div class="linked-to-items">
                  <span
                    v-for="country in item?.linked_countries"
                    :key="country.code"
                    class="linked-item"
                  >
                    <span class="green-dot" />
                    {{ country.name }}
                  </span>
                </div>
              </section>

              <section
                v-if="!item?.linked_countries.length && !item?.in_review_countries"
                class="linked-to"
              >
                <label>
                  {{ $t('linked_to') }}
                </label>
                <div class="linked-to-items">
                    -
                  </div>
              </section>
            </div>

            <v-btn
              class="account-card-actions"
              icon
            >
              <v-icon
                @click="onLabelClick(item)"
                class="material-icons-outlined"
              >
                edit
              </v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    data() {
      return {
        activeItem: {},
        isLoading: false,
        isDialogOpen: false
      };
    },
    computed: {
      billingSettings() {
        const { billingSettings } = this.$store.getters['businesses/activeItem'];
        return billingSettings || [];
      },
      billingCountriesByStatus() {
        const { billingCountriesByStatus } = this.$store.getters['businesses/activeItem'];
        return billingCountriesByStatus || [];
      },
      countriesMissingBilling() {
        const countriesMissingBilling = [];
        const billingCountriesByStatus = this.billingCountriesByStatus;

        for (const key in billingCountriesByStatus) {
          if (key === 'no_billing' || key === 'networks_denied') {
            countriesMissingBilling.push(...billingCountriesByStatus[key]);
          }
        }

        return countriesMissingBilling;
      },
      user() {
        return this.$store.getters['auth/user'];
      },
      isBusinessAdmin() {
        return this.user.is_business_admin;
      },
      missingItemsNames() {
        return this.countriesMissingBilling.length ? this.countriesMissingBilling.map(i => i.name).join(', ').replace(/, ([^,]*)$/, ' & $1') : '';
      },
    },
    watch: {
      isDialogOpen(newValue) {
        if (!newValue) {
          this.activeItem = {};
        }
      }
    },
    created() {
      if (this.isBusinessAdmin) {
        this.getBillingSettings();
        this.getBillingCountriesByStatus();
      }
    },
    methods: {
      getBillingSettings() {
        return this.$store.dispatch('businesses/getBillingSettings');
      },
      getBillingCountriesByStatus() {
        return this.$store.dispatch('businesses/getBillingCountriesByStatus');
      },
      onSubmitBillingAccount(query) {
        if (query.id) {
          return this.updateBillingAccount(query);
        }

        this.createBillingAccount(query);
      },
      createBillingAccount(query) {
        this.isLoading = true;

        return this.$store.dispatch('billing/create', query)
          .then(res => {
            this.isLoading = false;
            if (res.data) {
              this.isDialogOpen = false;
              this.getBillingSettings();
              this.getBillingCountriesByStatus();
            }
            return res;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      updateBillingAccount(query) {
        this.isLoading = true;

        return this.$store.dispatch('billing/update', query)
          .then(res => {
            this.isLoading = false;
            if (res.data) {
              this.isDialogOpen = false;
              this.getBillingSettings();
              this.getBillingCountriesByStatus();
            }
            return res;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      toggleEditDialog() {
        this.isDialogOpen = !this.isDialogOpen;
      },
      onLabelClick(item) {
        this.activeItem = item;
        this.toggleEditDialog();
      },
    }
  };
</script>

<style lang="scss">
  @use '../../../assets/styles/theme' as *;

  [data-component-group='billing-account-dialog'][data-component='add-new'] {
    .modal__backdrop > .modal__content {
      width: 650px;
      height: 100%;
      background-color: rgb(var(--v-theme-surface));

      .modal__header > .item-title {
        padding: 24px 34px 22px 34px;

        h1 {
          margin: 2px 0 0 12px;
          padding: 0;
          font-size: 27px;
        }
      }

      .modal__body {
        .title {
          display: none;
        }
      }
    }
  }

  [data-component-group="user-settings-panel"][data-component="billing-accounts"] {
    border-radius: $border-radius;

    .billing-accounts-wrapper {
      margin-bottom: 32px;
      padding: 32px;
      background-color: rgb(var(--v-theme-surface));
      border-radius: $border-radius;

      .v-alert {
        margin-bottom: 16px;

        .v-icon {
          margin-left: 10px;
        }
      }

      header {
        margin-bottom: 32px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 20px;
        font-weight: 500;
      }

      .empty-billing-accounts {
        margin-top: 28px;
        padding: 24px;
        height: 100px;
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
        align-items: center;
        font-size: 14px;
        color: #757575;
        background-color: #FCFCFC;
        border: 1px dashed #BDBDBD;
        border-radius: 4px;
        cursor: pointer;

        .v-theme--dark & {
          color: rgb(var(--v-theme-on-surface));
          background-color: rgba(var(--v-theme-on-surface), 0.06);
          border-color: rgba(var(--v-theme-on-surface), 0.24);
        }
      }

      .account-cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(360px, 100%), 1fr));
        gap: 16px;
      }

      .account-card-wrapper {
        border: 1px solid #BDBDBD;
        border-radius: $border-radius;
        font-size: 12px;
        overflow: hidden;
        position: relative;

        .warning-panel {
          align-items: center;
          background-color: #FFFDE7;
          border-radius: 4px 4px 0 0;
          color: #424242;
          display: flex;
          font-size: 12px;
          font-weight: 400;
          overflow: visible;
          padding: 8px 16px;

          .v-theme--dark & {
            color: rgb(var(--v-theme-on-surface));
            background-color: transparent;
          }

          .v-icon {
            margin-right: 8px;
          }
        }

        .advertiser-box-details {
          position: relative;
          padding-bottom: 6px;

          section {
            padding: 0 16px;
            font-size: 14px;
            font-weight: 400;
            line-height: 20px;
            color: #424242;

            .v-theme--dark & {
              color: rgb(var(--v-theme-on-surface));
            }

            label {
              font-size: 12px;
              line-height: 16px;
              color: #9E9E9E;
            }

            &:last-child {
              margin-bottom: 0;
            }
          }

          .card-name {
            padding-top: 12px;

            .card-name-item {
              color: #212121;
              font-size: 16px;
              font-weight: 700;
              line-height: 24px;

              .v-theme--dark & {
                color: rgb(var(--v-theme-on-surface));
              }
            }
          }

          .second-field {
            margin: 6px 0 11px 0;

            .second-field-item {
              margin-left: 2px;
              color: #424242;
              font-size: 12px;

              .v-theme--dark & {
                color: rgb(var(--v-theme-on-surface));
              }
            }
          }

          .linked-to {
            padding-bottom: 4px;

            .linked-to-items {
              margin-top: 4px;
              display: flex;
              flex-wrap: wrap;

              .linked-item {
                display: flex;
                align-items: center;
                margin: 0 4px 4px 0;
                padding: 2px 8px;
                font-size: 12px;
                font-weight: 500;
                line-height: 20px;
                background-color: #F5F5F5;
                border-radius: 50px;

                .v-theme--dark & {
                  background-color: transparent;
                  color: rgb(var(--v-theme-on-surface));
                }

                span {
                  margin-right: 4px;
                  width: 6px;
                  height: 6px;
                  border-radius: 50%;
                }

                .green-dot {
                  background-color: #43A047;
                }

                .orange-dot {
                  background-color: #FBC02D;
                }
              }
            }
          }
        }

        .pending {
          background-color: #F5F5F5;
          border: none;

          .v-theme--dark & {
            background-color: transparent;
            color: rgb(var(--v-theme-on-surface));
          }
        }

        .denied {
          background-color: #FDF5F5;
          border: 1px solid #E53935;

          .v-theme--dark & {
            background-color: transparent;
          }

          section {
            color: #E53935;
          }

          label {
            color: #E53935 !important;
          }
        }
      }

      .account-card-actions {
        position: absolute;
        right: 17px;
        top: 17px;
      }
    }
  }
</style>
